/**
 * Sinh khách theo kiểu và giờ game.
 *
 * Lịch khách đã trải lại ở M1 (sheet "Khách"): phủ kín 4–24 giờ game,
 * 0–4 giờ được phép đóng cửa, tổng lãi/ngày mỗi kiểu giữ nguyên.
 *
 * Công thức mua (master-plan 3.3):
 *   P(mua) ~ cầu cơ bản × f(chính sách) × hệ số danh tiếng × hệ số buổi
 * Trong sim: kỳ vọng lượt khách/giờ =
 *   luotKhachMoiGio × heSoLuuLuong × tyLeMatHangMo × heSoCau
 *   × danhTieng × heSoBuoi(M01) × heSoHieuSuat(offline).
 * Lãi mỗi món = trung bình lãi nhóm chính/phụ theo chính sách
 * (khớp giả định ước lượng của bảng tính — xem decisions-m1.md).
 */
import { layThamSoMoPhong } from './du-lieu.js';
import { heSoCau, laiNhomTheoChinhSach } from './gia.js';
import { heSoCauDatTheoGio, heSoLuuLuongTheoGio } from './khu-vuc.js';
import { heSoThoiTietLuuLuong, heSoThoiTietNhom } from './thoi-tiet.js';
import { matHangMo, truKho } from './kho.js';
import { soLuotNgauNhien, type HamRng } from './rng.js';
import type { DuLieuGame, KieuKhach, TrangThaiGame } from './types.js';

/** Giờ trong ngày (0–23) từ tổng giờ game. */
export function gioTrongNgay(tongGioGame: number): number {
  return ((Math.floor(tongGioGame) % 24) + 24) % 24;
}

/** Các kiểu khách đang mở tại một giờ trong ngày. */
export function kieuKhachMoTheoGio(gio: number, duLieu: DuLieuGame): KieuKhach[] {
  return duLieu.kieuKhach.filter((k) => gio >= k.gioBatDau && gio < k.gioKetThuc);
}

/** Số giờ trong ngày có ít nhất một kiểu khách mở (ngoài khung đóng cửa). */
export function demGioTrongKhach(duLieu: DuLieuGame): { gioTrong: number[]; gioMo: number[] } {
  const gioDongBatDau = layThamSoMoPhong(duLieu, 'M13');
  const gioDongKetThuc = layThamSoMoPhong(duLieu, 'M14');
  const gioTrong: number[] = [];
  const gioMo: number[] = [];
  for (let gio = 0; gio < 24; gio++) {
    if (gio >= gioDongBatDau && gio < gioDongKetThuc) continue; // được phép đóng cửa
    if (kieuKhachMoTheoGio(gio, duLieu).length === 0) {
      gioTrong.push(gio);
    } else {
      gioMo.push(gio);
    }
  }
  return { gioTrong, gioMo };
}

/** Kỳ vọng lượt khách trong 1 giờ game của một kiểu khách (M3: theo khu + thời tiết). */
export function kyVongLuotKhach(
  kieu: KieuKhach,
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  heSoHieuSuat: number,
): number {
  const cap = duLieu.capCuaHang[state.capCuaHang - 1];
  if (!cap) throw new Error(`Cấp cửa hàng lạ: ${state.capCuaHang}.`);
  const gio = gioTrongNgay(state.tongGioGame);
  // M3: lưu lượng theo khu/giờ (trung bình ngày khớp hệ số cấp) × thời tiết.
  const heSoKhu = heSoLuuLuongTheoGio(state, duLieu, gio);
  const heSoTt = heSoThoiTietLuuLuong(state, duLieu);
  // M3: Chợ đêm ít nhạy giá khi Đắt (bình quân gia quyền theo lưu lượng giờ đó).
  const heSoCauChinhSach = heSoCau(state.chinhSach, duLieu);
  const heSoCauDung =
    state.chinhSach === 'dat'
      ? heSoCauChinhSach * heSoCauDatTheoGio(state, duLieu, gio)
      : heSoCauChinhSach;
  return (
    kieu.luotKhachMoiGio *
    heSoKhu *
    cap.tyLeMatHangMo *
    heSoCauDung *
    state.danhTieng *
    layThamSoMoPhong(duLieu, 'M01') *
    heSoHieuSuat *
    heSoTt
  );
}

export interface KetQuaGioKhach {
  soLuot: number;
  monBan: number;
  laiGop: number;
  /** Tổng giá vốn các món đã bán — hoàn lại vào xu (bảo toàn giá trị). */
  vonHangBan: number;
  /** Lượt món muốn mua nhưng gặp hết hàng. */
  hetHang: number;
  monTheoMa: Record<string, number>;
  /** M3: tên kiểu khách -> số lượt (cho nhiệm vụ "phục vụ kiểu khách"). */
  luotTheoKieu: Record<string, number>;
  /** M3: tên nhóm hàng -> số món bán (cho nhiệm vụ "bán theo nhóm"). */
  monTheoNhom: Record<string, number>;
}

/**
 * Xử lý toàn bộ khách trong 1 giờ game (giờ hiện tại = state.tongGioGame).
 * Trừ kho thật qua kho.truKho; lãi tính theo trung bình nhóm.
 * M3: nhu cầu theo nhóm chịu hệ số thời tiết (trọng số chọn nhóm mua).
 */
export function xuLyGioKhach(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  rng: HamRng,
  heSoHieuSuat: number,
): KetQuaGioKhach {
  const ketQua: KetQuaGioKhach = {
    soLuot: 0,
    monBan: 0,
    laiGop: 0,
    vonHangBan: 0,
    hetHang: 0,
    monTheoMa: {},
    luotTheoKieu: {},
    monTheoNhom: {},
  };
  const gio = gioTrongNgay(state.tongGioGame);
  const gioDongBatDau = layThamSoMoPhong(duLieu, 'M13');
  const gioDongKetThuc = layThamSoMoPhong(duLieu, 'M14');
  if (gio >= gioDongBatDau && gio < gioDongKetThuc) return ketQua; // đóng cửa
  if (matHangMo(state, duLieu).length === 0) return ketQua;

  const nhomCuaMon = new Map(duLieu.matHang.map((m) => [m.ma, m.nhom]));
  for (const kieu of kieuKhachMoTheoGio(gio, duLieu)) {
    const soLuot = soLuotNgauNhien(kyVongLuotKhach(kieu, state, duLieu, heSoHieuSuat), rng);
    if (soLuot > 0) {
      ketQua.luotTheoKieu[kieu.ten] = (ketQua.luotTheoKieu[kieu.ten] ?? 0) + soLuot;
    }
    const laiChinh = laiNhomTheoChinhSach(kieu.nhomChinh, state.chinhSach, duLieu);
    const laiPhu = laiNhomTheoChinhSach(kieu.nhomPhu, state.chinhSach, duLieu);
    const laiMoiMon = (laiChinh + laiPhu) / 2;
    // M3: thời tiết làm lệch nhu cầu giữa nhóm chính/phụ.
    const wChinh = heSoThoiTietNhom(state, duLieu, kieu.nhomChinh);
    const wPhu = heSoThoiTietNhom(state, duLieu, kieu.nhomPhu);
    const pChinh = wChinh + wPhu > 0 ? wChinh / (wChinh + wPhu) : 0.5;
    const giaVonTheoMa = new Map(duLieu.matHang.map((m) => [m.ma, m.giaVon]));
    for (let i = 0; i < soLuot; i++) {
      ketQua.soLuot += 1;
      for (let j = 0; j < kieu.monMoiLuot; j++) {
        const nhomMua = rng() < pChinh ? kieu.nhomChinh : kieu.nhomPhu;
        const ma = truKho(state, duLieu, rng, nhomMua);
        if (ma === null) {
          ketQua.hetHang += 1;
        } else {
          ketQua.monBan += 1;
          ketQua.laiGop += laiMoiMon;
          ketQua.vonHangBan += giaVonTheoMa.get(ma) ?? 0;
          ketQua.monTheoMa[ma] = (ketQua.monTheoMa[ma] ?? 0) + 1;
          const nhom = nhomCuaMon.get(ma) ?? nhomMua;
          ketQua.monTheoNhom[nhom] = (ketQua.monTheoNhom[nhom] ?? 0) + 1;
        }
      }
    }
  }
  return ketQua;
}
